import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Table } from "antd";
import { ColumnType, TableProps } from "antd/lib/table";
import { concat, noop, size } from "lodash";
import ResizableTitle from "components/ResizableTitle";
import { Modal, message } from "components";
import useResizableTableHeader from "hooks/useResizableTableHeader";
import { getTotalDisplay } from "utils/getTotalDisplay";
import { AiccConversation } from "types/model/aiccCustomer";
import * as aiccAPI from "services/aiccCustomer";
import { useAICCStore } from "../../../../useAICCStore";
import { useAiccConversations } from "../../../../hooks/useAiccConversations";
import getTrailingColumns from "./columnConfig";
import styles from "./ConversationHistory.module.scss";

interface Props {
  subUserId: string;
  languageId: number;
  title: string;
  conversationType: string;
  leadingColumns: ColumnType<AiccConversation>[];
}

const PAGE_SIZE = 10;

function ConversationHistory({ subUserId, languageId, title, conversationType, leadingColumns }: Props) {
  const history = useHistory();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const setCachedSubUserDetail = useAICCStore((state) => state.setCachedSubUserDetail);

  const { conversations, total, isLoading, mutate } = useAiccConversations(
    subUserId,
    languageId,
    conversationType,
    page,
    pageSize
  );

  useEffect(() => {
    setPage(1);
  }, [subUserId, languageId, conversationType]);

  const handleReviewHistory = useCallback(
    (conversationId: string) => {
      history.push(`/aicc/customer/subusers/${subUserId}/languages/${languageId}/conversations/${conversationId}`);
    },
    [history, subUserId, languageId]
  );

  const handleDelete = useCallback(
    (conversationId: string) => {
      Modal.confirm({
        title: "Confirm",
        content: "Are you sure you want to delete this conversation record? (This action cannot be undone.)",
        onOk: () =>
          aiccAPI.deleteAiccConversation(subUserId, languageId, conversationId).then(() => {
            message.success("Deleted the conversation record successfully.");
            // Conversation counts live on the sub-user row, so the parallel-request
            // cache in useAICCStore is stale once a record is gone.
            setCachedSubUserDetail(undefined);
            // Deleting the last row of a trailing page would leave the table on an
            // empty page, so step back instead of refetching the current one.
            if (size(conversations) === 1 && page > 1) {
              setPage(page - 1);
              return;
            }
            // Not returned: antd holds the dialog in its loading state until onOk's promise
            // settles, so awaiting the refresh kept the modal open behind its own success toast.
            mutate().catch(noop);
          }),
      });
    },
    [subUserId, languageId, conversations, page, mutate, setCachedSubUserDetail]
  );

  const handleRowClick: TableProps<AiccConversation>["onRow"] = (record) => ({
    onClick: () => handleReviewHistory(record.id),
  });

  const columns = useMemo(() => concat(leadingColumns, getTrailingColumns(handleDelete)), [
    leadingColumns,
    handleDelete,
  ]);

  const columnsToDisplay = useResizableTableHeader<AiccConversation>(columns, 100, 500);

  return (
    <div>
      <div className={styles.sectionTitle}>{title}</div>

      <Table<AiccConversation>
        scroll={{ x: "100%" }}
        loading={isLoading}
        dataSource={conversations}
        columns={columnsToDisplay}
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        rowKey={(record) => record.id}
        onRow={handleRowClick}
        pagination={{
          showTotal: getTotalDisplay,
          current: page,
          pageSize,
          total,
          defaultCurrent: 1,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize ?? PAGE_SIZE);
          },
        }}
      />
    </div>
  );
}

export default ConversationHistory;
